import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAdmin } from "./AdminLayout";
import { useAdminLang, useLabel } from "./adminLang";
import { Plus, Pencil, Trash2, X, Upload } from "lucide-react";

const EN_TITLES = {
  posts: "News", jobs: "Job openings", partners: "Partners (published after written agreement)",
  team: "Team & founder", faqs: "FAQ", documents: "Downloadable documents (company profiles…)",
  locations: "Offices", pages: "Pages & SEO metadata",
};

// Field types: text | textarea | checkbox | number | select | image | file
const ENTITIES = {
  posts: {
    label: "Actualités",
    fields: [
      { key: "slug", label: "Slug (URL)", type: "text" },
      { key: "category", label: "Catégorie", type: "select", options: ["actualites", "infrastructure", "innovation", "connectivite_rurale", "entreprises", "secteur_public", "afrique_numerique"] },
      { key: "title_fr", label: "Titre FR", type: "text" }, { key: "title_en", label: "Titre EN", type: "text" },
      { key: "summary_fr", label: "Résumé FR", type: "textarea" }, { key: "summary_en", label: "Résumé EN", type: "textarea" },
      { key: "body_fr", label: "Corps FR", type: "textarea" }, { key: "body_en", label: "Corps EN", type: "textarea" },
      { key: "image_url", label: "Image", type: "image" },
      { key: "published", label: "Publié", type: "checkbox" },
    ],
    display: (r) => r.title_fr,
  },
  jobs: {
    label: "Offres d'emploi",
    fields: [
      { key: "title_fr", label: "Titre FR", type: "text" }, { key: "title_en", label: "Titre EN", type: "text" },
      { key: "location", label: "Lieu", type: "text" }, { key: "contract_type", label: "Type de contrat", type: "text" },
      { key: "description_fr", label: "Description FR", type: "textarea" }, { key: "description_en", label: "Description EN", type: "textarea" },
      { key: "published", label: "Publié", type: "checkbox" },
    ],
    display: (r) => r.title_fr,
  },
  partners: {
    label: "Partenaires (publication après accord écrit)",
    fields: [
      { key: "name", label: "Nom", type: "text" },
      { key: "logo_url", label: "Logo", type: "image" },
      { key: "website", label: "Site web", type: "text" },
      { key: "order", label: "Ordre", type: "number" },
      { key: "published", label: "Publié (accord confirmé)", type: "checkbox" },
    ],
    display: (r) => r.name,
  },
  team: {
    label: "Équipe & fondateur",
    fields: [
      { key: "name", label: "Nom", type: "text" },
      { key: "role_fr", label: "Rôle FR", type: "text" }, { key: "role_en", label: "Rôle EN", type: "text" },
      { key: "bio_fr", label: "Bio FR", type: "textarea" }, { key: "bio_en", label: "Bio EN", type: "textarea" },
      { key: "photo_url", label: "Photo", type: "image" },
      { key: "is_founder", label: "Fondateur", type: "checkbox" },
      { key: "order", label: "Ordre", type: "number" },
      { key: "published", label: "Publié", type: "checkbox" },
    ],
    display: (r) => r.name,
  },
  faqs: {
    label: "FAQ",
    fields: [
      { key: "page", label: "Page (general, fibre-optique…)", type: "text" },
      { key: "question_fr", label: "Question FR", type: "text" }, { key: "question_en", label: "Question EN", type: "text" },
      { key: "answer_fr", label: "Réponse FR", type: "textarea" }, { key: "answer_en", label: "Réponse EN", type: "textarea" },
      { key: "order", label: "Ordre", type: "number" },
      { key: "published", label: "Publié", type: "checkbox" },
    ],
    display: (r) => r.question_fr,
  },
  documents: {
    label: "Documents téléchargeables (profils d'entreprise…)",
    fields: [
      { key: "title", label: "Titre", type: "text" },
      { key: "kind", label: "Type", type: "select", options: ["company_profile", "other"] },
      { key: "locale", label: "Langue", type: "select", options: ["fr", "en"] },
      { key: "file_path", label: "Fichier (PDF…)", type: "file" },
      { key: "published", label: "Publié", type: "checkbox" },
    ],
    display: (r) => `${r.title} (${r.locale})`,
  },
  locations: {
    label: "Bureaux",
    fields: [
      { key: "name", label: "Nom", type: "text" },
      { key: "data.address_fr", label: "Adresse FR", type: "textarea" },
      { key: "data.address_en", label: "Adresse EN", type: "textarea" },
      { key: "data.phone", label: "Téléphone", type: "text" },
      { key: "data.email", label: "E-mail", type: "text" },
      { key: "is_primary", label: "Siège principal", type: "checkbox" },
    ],
    display: (r) => r.name,
  },
  pages: {
    label: "Pages & métadonnées SEO",
    fields: [
      { key: "slug", label: "Slug", type: "text" },
      { key: "locale", label: "Langue", type: "select", options: ["fr", "en"] },
      { key: "title", label: "Titre", type: "text" },
      { key: "meta_title", label: "Meta title", type: "text" },
      { key: "meta_description", label: "Meta description", type: "textarea" },
      { key: "published", label: "Publié", type: "checkbox" },
    ],
    display: (r) => `${r.slug} (${r.locale})`,
  },
};

const getNested = (obj, key) => key.split(".").reduce((o, k) => (o || {})[k], obj) ?? "";
const setNested = (obj, key, value) => {
  const clone = { ...obj };
  const parts = key.split(".");
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = { ...(cur[parts[i]] || {}) }; cur = cur[parts[i]]; }
  cur[parts[parts.length - 1]] = value;
  return clone;
};

const Content = () => {
  const { entity } = useParams();
  const { canWrite } = useAdmin();
  const { tr } = useAdminLang();
  const L = useLabel();
  const cfg = ENTITIES[entity];
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | row
  const [uploading, setUploading] = useState("");

  const load = () => api.get(`/admin/${entity}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    setRows([]); setEditing(null); load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  if (!cfg) return <p className="text-white/40">{tr("Entité inconnue.", "Unknown entity.")}</p>;

  const save = async () => {
    const clean = { ...editing };
    delete clean.id; delete clean.created_at; delete clean.updated_at;
    if (editing.id) await api.patch(`/admin/${entity}/${editing.id}`, clean);
    else await api.post(`/admin/${entity}`, clean);
    setEditing(null);
    api.get(`/admin/${entity}`).then((r) => setRows(r.data));
  };

  const remove = async (id) => {
    if (!window.confirm(tr("Confirmer la suppression ?", "Confirm deletion?"))) return;
    await api.delete(`/admin/${entity}/${id}`);
    api.get(`/admin/${entity}`).then((r) => setRows(r.data));
  };

  const upload = async (fieldKey, file, sub) => {
    if (!file) return;
    setUploading(fieldKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/upload?sub=${sub || entity}`, fd);
      setEditing((prev) => setNested(prev, fieldKey, res.data.path));
    } finally { setUploading(""); }
  };

  const inputCls = "w-full bg-white/5 border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF6600]";

  return (
    <div data-testid={`admin-content-${entity}`}>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-black text-white">{tr(cfg.label, EN_TITLES[entity] || cfg.label)}</h1>
        {canWrite && (
          <button onClick={() => setEditing({})} data-testid={`content-create-${entity}`}
            className="inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-xs font-black rounded-full px-5 py-2.5 transition-colors">
            <Plus size={14} /> {tr("Créer", "Create")}
          </button>
        )}
      </div>

      <div className="mt-6 bg-[#101214] border border-white/10 rounded-2xl divide-y divide-white/5">
        {rows.length === 0 && <p className="px-6 py-10 text-center text-white/35 text-sm">{tr("Aucune donnée.", "No data.")}</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`content-row-${r.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white truncate">{cfg.display(r)}</p>
              <p className="text-xs text-white/40">
                {"published" in r && (r.published ? <span className="text-[#FF9900] font-bold">{tr("Publié", "Published")}</span> : <span>{tr("Brouillon / non publié", "Draft / unpublished")}</span>)}
                {"order" in r && r.order != null && <span> · {tr("ordre", "order")} {r.order}</span>}
              </p>
            </div>
            {canWrite && (
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(r)} data-testid={`content-edit-${r.id}`} className="p-2 text-white/50 hover:text-[#FF9900] transition-colors" aria-label={tr("Modifier", "Edit")}><Pencil size={16} /></button>
                <button onClick={() => remove(r.id)} data-testid={`content-delete-${r.id}`} className="p-2 text-white/50 hover:text-red-400 transition-colors" aria-label={tr("Supprimer", "Delete")}><Trash2 size={16} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setEditing(null)} data-testid="content-editor-panel">
          <div className="w-full max-w-xl bg-[#101214] border-l border-white/10 h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black text-white">{editing.id ? tr("Modifier", "Edit") : tr("Créer", "Create")}</h2>
              <button onClick={() => setEditing(null)} className="text-white/50 hover:text-white p-1"><X size={20} /></button>
            </div>
            <div className="mt-6 space-y-4">
              {cfg.fields.map((f) => {
                const val = getNested(editing, f.key);
                const setVal = (v) => setEditing((prev) => setNested(prev, f.key, v));
                return (
                  <div key={f.key}>
                    <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-1.5">{L(f.label)}</label>
                    {f.type === "textarea" && <textarea rows={4} value={val} onChange={(e) => setVal(e.target.value)} className={inputCls} data-testid={`field-${f.key}`} />}
                    {f.type === "text" && <input value={val} onChange={(e) => setVal(e.target.value)} className={inputCls} data-testid={`field-${f.key}`} />}
                    {f.type === "number" && <input type="number" value={val} onChange={(e) => setVal(Number(e.target.value))} className={inputCls} data-testid={`field-${f.key}`} />}
                    {f.type === "checkbox" && (
                      <label className="flex items-center gap-2.5 text-sm text-white/70">
                        <input type="checkbox" checked={!!val} onChange={(e) => setVal(e.target.checked)} className="w-4 h-4 accent-[#FF6600]" data-testid={`field-${f.key}`} />
                        {L(f.label)}
                      </label>
                    )}
                    {f.type === "select" && (
                      <select value={val} onChange={(e) => setVal(e.target.value)} className={inputCls} data-testid={`field-${f.key}`}>
                        <option value="">—</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                    {(f.type === "image" || f.type === "file") && (
                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-bold text-[#FF9900] border border-[#FF6600]/40 rounded-xl px-4 py-2.5 hover:bg-[#FF6600]/10">
                          <Upload size={14} /> {uploading === f.key ? tr("Envoi…", "Uploading…") : tr("Téléverser", "Upload")}
                          <input type="file" className="hidden" accept={f.type === "image" ? "image/*" : ".pdf,.doc,.docx,.pptx"}
                            onChange={(e) => upload(f.key, e.target.files?.[0], f.type === "image" ? "images" : "docs")} data-testid={`upload-${f.key}`} />
                        </label>
                        {val && <span className="text-[11px] text-white/40 truncate max-w-[180px]">{val}</span>}
                        {f.type === "image" && val && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${val}`} alt="" className="h-10 w-10 rounded-lg object-cover border border-white/10" />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex gap-3">
              <button onClick={save} data-testid="content-save-button"
                className="bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-xs font-black rounded-full px-6 py-3 transition-colors">{tr("Enregistrer", "Save")}</button>
              <button onClick={() => setEditing(null)} className="text-xs font-bold text-white/50 hover:text-white px-4">{tr("Annuler", "Cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Content;
